/**
 * Planning Studio (owner, 2026-11-03): one screen per sales order that walks furniture planning through fixed stages —
 * Jobs → Route & minutes → People & dates ┃ Materials → Approval → Released. Everything it offers comes from
 * Production Setup and is copied into the job when planned.
 */
import { z } from 'zod';
import type { MinutesEntryMode, OpLocation } from './mfg';

export const STUDIO_STAGES = ['JOBS', 'ROUTE', 'PEOPLE', 'MATERIALS', 'APPROVAL', 'RELEASED'] as const;
export type StudioStage = (typeof STUDIO_STAGES)[number];
export const STUDIO_STAGE_LABELS: Record<StudioStage, string> = {
  JOBS: 'Jobs',
  ROUTE: 'Route & minutes',
  PEOPLE: 'People & dates',
  MATERIALS: 'Materials',
  APPROVAL: 'Approval',
  RELEASED: 'Released',
};
/** done ✓ · active (in progress) · waiting (its turn has not come) · blocked (something stops it; the reason says what). */
export type StageState = 'done' | 'active' | 'waiting' | 'blocked';
export interface StageStatus {
  stage: StudioStage;
  state: StageState;
  /** Plain words: what is done, what is missing, or why it is blocked. */
  note: string;
  /** e.g. 3 of 5 jobs. */
  progress?: { done: number; of: number };
}

export const JOB_GROUP_MODES = ['ITEM', 'SUBCATEGORY', 'ORDER'] as const;
export type JobGroupMode = (typeof JOB_GROUP_MODES)[number];
export const PLAN_MODES = ['SIMPLE', 'DETAILED'] as const;
export type PlanMode = (typeof PLAN_MODES)[number];

export interface StudioOp {
  operationTypeId: string;
  code: string;
  name: string;
  seq: number;
  isMain: boolean;
  required: boolean;
  colour: string;
  icon: string;
  minutesEntry: MinutesEntryMode;
  location: OpLocation;
  /** PRODUCTION, or a milestone carried out by DELIVERY / INSTALLATION (not booked in the studio). */
  handledBy?: 'PRODUCTION' | 'DELIVERY' | 'INSTALLATION';
  needsMaterial?: boolean;
}

export interface StudioCategory {
  id: string;
  code: string;
  name: string;
  plannerMayReorder: boolean;
  /** The category's own setting, or the planning rule when it follows the rule. */
  allDetailedRequired: boolean;
  skipMainAllowed: boolean;
  /** The plan type offered first (category, else the planning rule). */
  defaultPlanMode: PlanMode;
  operations: StudioOp[];
}

export interface StudioLine {
  lineId: string;
  lineNo: number;
  item: { id: string; code: string; name: string; uom: string; category: string } | null;
  description: string;
  qty: number;
  /** Quantity already in jobs. */
  planned: number;
  /** Quantity marked produced outside the studio (old system). */
  outside: number;
  open: number;
}

export interface StudioWarning {
  jobItemId: string;
  operationTypeId: string;
  /** e.g. "Paint: 60,000 min per piece — usually about 360 (12 past jobs)". */
  message: string;
}

export interface SkipRequestRow {
  id: string;
  jobItemId: string;
  operationTypeId: string;
  operation: string;
  item: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedBy: string | null;
  requestedAt: string;
  decidedBy: string | null;
  note: string;
}

export interface StudioJob {
  id: string;
  number: string | null;
  status: string;
  stage: string;
  version: number;
  groupMode: JobGroupMode | '';
  groupLabel: string;
  difficulty: string;
  routeId: string | null;
  routeName: string | null;
  planMode: PlanMode | null;
  /** Stage 1 can no longer change freely: people are booked (a controlled replan is needed). */
  locked: boolean;
  lockReason: string | null;
  /** The category changed in Production Setup since this job copied it (offer "update to latest setup" while unbooked). */
  outdated: boolean;
  /** label: "L3 · TABLE"; detail: the order line's description / size when it says more than the item name. */
  items: { id: string; soLineId: string; lineNo: number; item: string; label: string; detail: string; code: string; qty: number; uom: string }[];
  /** The category as copied into this job, with what the plan uses. */
  operations: (StudioOp & { inPlan: boolean })[];
  plan: { jobItemId: string; operationTypeId: string; entry: MinutesEntryMode; perPiece: number | null; minutes: number; skipped: boolean }[];
  skipRequests: SkipRequestRow[];
  totalMinutes: number;
  /** What stage 1 still needs (empty = done). */
  missing: string[];
  warnings: StudioWarning[];
  /** What stops the job from going for approval / release (empty = ready). */
  gaps: string[];
}

export interface StudioOrder {
  order: { id: string; number: string | null; customer: string | null; orderDate: string; committedDate: string | null; drawingStatus: string; productionStatus: string };
  rules: { jobModes: JobGroupMode[]; difficultyLabels: string[]; unusualMinutesPct: number };
  lines: StudioLine[];
  jobs: StudioJob[];
  categories: StudioCategory[];
  /** Typical minutes per piece by operation, for the warning and as a hint next to the field. */
  typical: Record<string, { perPiece: number; samples: number; source: 'PAST' | 'SETUP' }>;
  stages: StageStatus[];
  milestones: OrderMilestones;
  can: { plan: boolean; authoriseSkip: boolean; markOutside: boolean };
}

export interface StudioOrderRow {
  orderId: string;
  number: string | null;
  customer: string | null;
  orderDate: string;
  committedDate: string | null;
  lines: number;
  openQty: number;
  jobs: number;
  stages: StageStatus[];
  /** The first stage not done. */
  next: StudioStage | null;
  /** What blocks it (a blocked stage's note), if anything. */
  blocker: string | null;
  /** The realistic finish (last booked work), ISO, or null. */
  finish: string | null;
  late: boolean;
  /** Every furniture line produced outside the studio. */
  outside: boolean;
}

export const studioMakeJobsSchema = z.object({
  mode: z.enum(JOB_GROUP_MODES),
  lineIds: z.array(z.string().uuid()).min(1, 'Choose at least one order line').max(200),
});
export type StudioMakeJobsInput = z.infer<typeof studioMakeJobsSchema>;

const mins = z.coerce.number().int('Whole minutes').min(0, 'Minutes cannot be negative').max(1_000_000, 'Too many minutes');
export const studioRouteSchema = z.object({
  version: z.number().int(),
  routeId: z.string({ error: 'Choose the category' }).uuid('Choose the category'),
  planMode: z.enum(PLAN_MODES),
  difficulty: z.string().trim().max(20).default(''),
  /** The operations in the plan, in order (Simple: the main ones; Detailed: the chosen ones). */
  operations: z.array(z.string().uuid()).min(1, 'The plan needs at least one operation').max(40),
  /**
   * Only minutes changed (same category, plan type, difficulty and operations): save just the boxes sent, without the
   * whole-job version check — so planners of different sections of one job do not collide.
   */
  partial: z.boolean().default(false),
  /** Minutes per item × operation: per piece (× quantity) or the total. */
  minutes: z
    .array(z.object({ jobItemId: z.string().uuid(), operationTypeId: z.string().uuid(), entry: z.enum(['PER_PIECE', 'TOTAL']), value: mins.nullable() }))
    .max(2000),
});
export type StudioRouteInput = z.infer<typeof studioRouteSchema>;

export const skipRequestSchema = z.object({
  jobItemId: z.string().uuid(),
  operationTypeId: z.string().uuid(),
  reason: z.string().trim().min(5, 'Say why (at least 5 characters)').max(300),
});
export type SkipRequestInput = z.infer<typeof skipRequestSchema>;
export const skipDecisionSchema = z.object({ approve: z.boolean(), note: z.string().trim().max(300).default('') }).refine((v) => v.approve || v.note.length >= 3, { path: ['note'], message: 'Say why it is refused' });
export type SkipDecisionInput = z.infer<typeof skipDecisionSchema>;

// ---------- Stage: People & dates ----------

/** One item × operation of a job: needed vs booked, who, when, and what must end before it may start. */
export interface PeopleRow {
  jobItemId: string;
  item: string;
  detail: string;
  qty: number;
  operationTypeId: string;
  operation: string;
  colour: string;
  icon: string;
  seq: number;
  needed: number;
  booked: number;
  skipped: boolean;
  location: 'FACTORY' | 'SITE';
  canOverlap: boolean;
  people: { employeeId: string; name: string; minutes: number }[];
  bookings: { id: string; employeeId: string; name: string; start: string; end: string; minutes: number; overtime: boolean; started: boolean }[];
  /** Actual minutes logged (job cards / shop floor) on this item × operation; plan and actual stay apart. */
  actual: number;
  /** Responsible for People & dates of this operation's section (empty = anyone planning). */
  responsible: string[];
  /** Who booked last, and when (ISO). */
  doneBy: string | null;
  doneAt: string | null;
  /** First and last booked moment (ISO), for the Gantt. */
  from: string | null;
  to: string | null;
  /** The previous operation of the same item: this one may not start before it ends (unless overlap is allowed). */
  prev: { operation: string; ends: string | null; booked: boolean } | null;
}

export interface AbsenceAlert {
  allocationId: string;
  jobItemId: string;
  operationTypeId: string;
  employee: string;
  day: string;
  reason: 'NO_ATTENDANCE' | 'LEAVE' | 'HOLIDAY';
  minutes: number;
}

export interface StudioPeople {
  jobId: string;
  rows: PeopleRow[];
  /** A booked person absent (no attendance) or on approved leave / a holiday on a booked day. */
  alerts: AbsenceAlert[];
  rules: { allowOverlapDefault: boolean; sundayOvertime: boolean; shiftTimesOnly: boolean };
  /** When the last booked work of all jobs of the order ends (ISO), and the dates on the order. */
  finish: string | null;
  order: { committedDate: string | null; completionDate: string | null };
  can: { book: boolean };
}

export interface BookingCandidate {
  id: string;
  code: string;
  name: string;
  section: string | null;
  /** 1 learner · 2 skilled · 3 expert; null when not in the Skills Matrix. */
  skill: number | null;
  /** Booked minutes this month / working minutes this month. */
  load: { booked: number; available: number };
}

export interface CalendarDay {
  day: string;
  weekday: number;
  state: 'free' | 'partial' | 'full' | 'off';
  off: 'holiday' | 'leave' | 'weekly_off' | null;
  /** Minutes of the shift that day (0 when off) and minutes already booked. */
  available: number;
  booked: number;
  /** A weekly off that can be enabled as overtime (planning rule). */
  canOvertime: boolean;
  /** Before the previous operation of this item ends: not bookable unless overlap is allowed. */
  beforePrev: boolean;
  past: boolean;
  bookings: { label: string; from: string; to: string; mine: boolean }[];
}

export interface EmployeeCalendar {
  employeeId: string;
  month: string;
  shift: { start: string; end: string };
  days: CalendarDay[];
  /** The previous operation of the item ends here (ISO), or null. */
  earliest: string | null;
}

export const bookSchema = z.object({
  jobItemId: z.string().uuid(),
  operationTypeId: z.string().uuid(),
  employeeId: z.string({ error: 'Choose the employee' }).uuid('Choose the employee'),
  days: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1, 'Click the days to book').max(62),
  /** Weekly offs in `days` that the planner enabled as overtime. */
  overtimeDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(31).default([]),
  allowOverlap: z.boolean().default(false),
});
export type BookInput = z.infer<typeof bookSchema>;
export interface BookResult {
  booked: number;
  remaining: number;
  notes: string[];
  people: StudioPeople;
}

// ---------- Stage: Materials ----------

export const MATERIAL_PLAN_KINDS = ['PLAN', 'REVISION', 'ADDITIONAL'] as const;
export type MaterialPlanKind = (typeof MATERIAL_PLAN_KINDS)[number];
export const MATERIAL_PLAN_KIND_LABELS: Record<MaterialPlanKind, string> = { PLAN: 'Material plan', REVISION: 'Revision', ADDITIONAL: 'Additional material' };

const qtyIn = z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: 'Enter the quantity' }).max(1e7)).refine((q) => q > 0, 'Quantity must be above 0');
export const materialPlanSchema = z
  .object({
    version: z.number().int().optional(),
    kind: z.enum(MATERIAL_PLAN_KINDS),
    reason: z.string().trim().max(500).default(''),
    lines: z
      .array(
        z.object({
          jobItemId: z.string().uuid(),
          operationTypeId: z.string().uuid(),
          itemId: z.string({ error: 'Choose the item' }).uuid('Choose the item'),
          qty: qtyIn,
          unit: z.enum(['STOCK', 'PURCHASE']).default('STOCK'),
          notes: z.string().trim().max(300).default(''),
          copiedFromJobId: z.string().uuid().nullable().default(null),
        }),
      )
      .min(1, 'Add at least one material line')
      .max(500),
  })
  .refine((v) => v.kind === 'PLAN' || v.reason.length >= 5, { path: ['reason'], message: 'Say what changed and why (at least 5 characters)' });
export type MaterialPlanInput = z.infer<typeof materialPlanSchema>;

export interface MaterialLineView {
  id: string;
  jobItemId: string;
  operationTypeId: string;
  item: { id: string; code: string; name: string; uom: string; purchase: { uom: string; factor: number } | null };
  qty: number;
  enteredQty: number;
  unit: 'STOCK' | 'PURCHASE';
  notes: string;
  copiedFromJobId: string | null;
}

export interface MaterialPlanDoc {
  id: string;
  number: string | null;
  kind: MaterialPlanKind;
  status: string;
  version: number;
  reason: string;
  date: string;
  by: string | null;
  /** The send-back comment (rectification), when sent back. */
  comment: string | null;
  lines: MaterialLineView[];
}

export interface StudioMaterials {
  jobId: string;
  /** Opens once route & minutes are complete; the reason otherwise. */
  open: boolean;
  openNote: string;
  /** Item × operations of the job (in plan order) and where their material stands. */
  cells: { jobItemId: string; item: string; detail: string; qty: number; operationTypeId: string; operation: string; colour: string; icon: string; state: 'none' | 'draft' | 'pending' | 'approved'; noMaterial: boolean; responsible: string[]; doneBy: string | null; doneAt: string | null; approvedBy: string | null }[];
  /** Approved material per item × operation (all approved plans, revisions and additional material together). */
  approved: MaterialLineView[];
  plans: MaterialPlanDoc[];
  /** The plan being typed (draft or sent back), if any. */
  draft: MaterialPlanDoc | null;
  needsApproval: boolean;
  /** Order requisitions (Purchase & Inventory › Order Material): requisition lines raised against this order so far. */
  requisitions: { lines: number; items: number };
  can: { edit: boolean; submit: boolean };
}

export interface CopySource {
  jobId: string;
  jobNumber: string | null;
  order: string | null;
  customer: string | null;
  item: string;
  detail: string;
  operationTypeId: string;
  operation: string;
  approvedAt: string | null;
  lines: { itemId: string; code: string; name: string; uom: string; qty: number }[];
}

// ---------- Hand-over: Delivery & Installation milestones, produced outside the studio ----------

export interface ReadyLine { lineId: string; lineNo: number; item: string; ready: number; uom: string; source: 'STUDIO' | 'OUTSIDE' }
export interface ReadyOrder { orderId: string; number: string | null; customer: string | null; committedDate: string | null; lines: ReadyLine[] }
export interface OrderMilestones {
  /** Last factory operation booked (YYYY-MM-DD). */
  production: string | null;
  /** Delivery: the last posted delivery note, else production + the dispatch days rule (planned). */
  delivery: { date: string | null; planned: boolean; notes: { id: string; number: string | null; status: string; date: string }[] };
  /** Installation (site operations): the last scheduled visit, else delivery + the installation days rule (planned). */
  installation: { needed: boolean; date: string | null; planned: boolean; orders: { id: string; number: string | null; stage: string; visitEnd: string | null }[] };
  /** When the customer has everything. */
  finish: string | null;
}
export const markOutsideSchema = z.object({
  orderIds: z.array(z.string().uuid()).max(500).default([]),
  lineIds: z.array(z.string().uuid()).max(2000).default([]),
  reason: z.string().trim().min(5, 'Say why (at least 5 characters)').max(300),
}).refine((v) => v.orderIds.length + v.lineIds.length > 0, { path: ['orderIds'], message: 'Choose orders or lines' });
export type MarkOutsideInput = z.infer<typeof markOutsideSchema>;

// ---------- My work (Shop Floor / Job Card Entry) ----------

export interface MyBooking {
  allocationId: string;
  day: string;
  from: string;
  to: string;
  minutes: number;
  overtime: boolean;
  job: { id: string; number: string | null; label: string; customer: string | null };
  item: string;
  operationId: string;
  operation: string;
  colour: string;
  icon: string;
  status: 'PLANNED' | 'RELEASED' | 'IN_PROGRESS' | 'DONE';
  previousDone: boolean;
  /** Work already logged by this person in the booked time. */
  logged: number;
}
export interface MyWork {
  employee: { id: string; name: string } | null;
  range: { from: string; to: string };
  bookings: MyBooking[];
}

// ---------- Planning responsibilities ----------

export const PLAN_STAGES = ['ROUTE', 'PEOPLE', 'MATERIALS', 'APPROVE_MATERIAL'] as const;
/** Inquiry Studio steps that join the responsibilities (review is per section; the others use "Every section"). */
export const INQ_RESP_STAGES = ['INQ_INQUIRY', 'INQ_DRAWINGS', 'INQ_DRAWERS', 'INQ_REVIEW', 'INQ_DISPATCH', 'INQ_COSTING', 'INQ_MARGIN'] as const;
export type PlanStageKey = (typeof PLAN_STAGES)[number] | (typeof INQ_RESP_STAGES)[number];
export const PLAN_STAGE_LABELS: Record<PlanStageKey, string> = { ROUTE: 'Jobs + route & minutes', PEOPLE: 'People & dates', MATERIALS: 'Materials', APPROVE_MATERIAL: 'Approve material plan', INQ_INQUIRY: 'Inquiry (sales)', INQ_DRAWINGS: 'Drawings Head (assigns)', INQ_DRAWERS: 'Drawings team', INQ_REVIEW: 'Production review', INQ_DISPATCH: 'Dispatch date', INQ_COSTING: 'Pre-costing', INQ_MARGIN: 'Approve low margin' };

export const responsibilitySchema = z.object({
  stage: z.enum([...PLAN_STAGES, ...INQ_RESP_STAGES]),
  /** null = every section. */
  sectionId: z.string().uuid().nullable(),
  userIds: z.array(z.string().uuid()).max(50).default([]),
  roleIds: z.array(z.string().uuid()).max(50).default([]),
  backupUserIds: z.array(z.string().uuid()).max(20).default([]),
  version: z.number().int().optional(),
});
export type ResponsibilityInput = z.infer<typeof responsibilitySchema>;

export interface ResponsibilityRow extends ResponsibilityInput {
  id: string | null;
  section: string | null;
  users: { id: string; name: string; onLeave: boolean }[];
  roles: { id: string; name: string }[];
  backups: { id: string; name: string; onLeave: boolean }[];
}
export interface ResponsibilityMatrix {
  sections: { id: string; name: string }[];
  rows: ResponsibilityRow[];
  people: { id: string; name: string }[];
  roles: { id: string; name: string }[];
}

/** A planning task waiting on someone (My planning tasks / workload). */
export interface PlanTask {
  stage: PlanStageKey;
  orderId: string;
  orderNumber: string | null;
  customer: string | null;
  jobId: string | null;
  job: string | null;
  sectionId: string | null;
  section: string | null;
  /** Since when it waits (ISO). */
  since: string;
  /** Hours past the time limit of the stage (0 = on time). */
  lateHours: number;
  /** Who it waits on (names). */
  owners: string[];
  what: string;
  /** Link: the studio with the job / the material plan. */
  planId: string | null;
}
export interface MyTasks {
  waiting: PlanTask[];
  /** Material plans I sent that wait for approval. */
  sent: { id: string; number: string | null; job: string | null; orderId: string; since: string }[];
}
export interface Workload {
  people: { userId: string; name: string; tasks: number; late: number; oldest: string | null }[];
  unassigned: number;
}
